import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  apiService,
  PublicBusiness,
  SuperAdminOffering,
  SuperAdminRedemptionFilters,
  SuperAdminRedemptionItem,
  SuperAdminRedemptionsResponse,
} from '../services/api';
import './SuperAdminViews.css';
import './AllRedemptionsView.css';

// Page sizes the user can choose from
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

// Date range presets that quickly seed the redeemed_after / redeemed_before filters
type DateRangePreset = 'all' | '7d' | '30d' | '90d' | 'ytd' | 'custom';

const DATE_RANGE_OPTIONS: { value: DateRangePreset; label: string }[] = [
  { value: '30d', label: 'Last 30 days' },
  { value: '7d', label: 'Last 7 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'ytd', label: 'Year to date' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Custom range' },
];

// Candidate page sizes for the trend fetcher. We start with the largest size
// and step down if the API silently ignores it / returns no items. 20 is the
// API default and is therefore guaranteed to work.
const TREND_PAGE_SIZE_CANDIDATES = [100, 50, 20];
// Hard cap on how many redemptions we will pull for the trend chart. Keeps
// the request fan-out bounded even when the filtered set is huge.
const TREND_MAX_RECORDS = 2000;

// ---------- Helpers ----------

const toIsoStartOfDay = (date: Date): string => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};

const toIsoEndOfDay = (date: Date): string => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
};

const formatYmd = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const parseYmdToDate = (ymd: string): Date | null => {
  if (!ymd) return null;
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const presetToDateRange = (
  preset: DateRangePreset
): { after?: string; before?: string } => {
  const now = new Date();
  switch (preset) {
    case '7d': {
      const start = new Date();
      start.setDate(now.getDate() - 6);
      return { after: toIsoStartOfDay(start), before: toIsoEndOfDay(now) };
    }
    case '30d': {
      const start = new Date();
      start.setDate(now.getDate() - 29);
      return { after: toIsoStartOfDay(start), before: toIsoEndOfDay(now) };
    }
    case '90d': {
      const start = new Date();
      start.setDate(now.getDate() - 89);
      return { after: toIsoStartOfDay(start), before: toIsoEndOfDay(now) };
    }
    case 'ytd': {
      const start = new Date(now.getFullYear(), 0, 1);
      return { after: toIsoStartOfDay(start), before: toIsoEndOfDay(now) };
    }
    case 'all':
      return {};
    case 'custom':
    default:
      return {};
  }
};

const displayName = (r: SuperAdminRedemptionItem): string => {
  const full = [r.first_name, r.last_name].filter(Boolean).join(' ').trim();
  return (
    r.user_name ||
    full ||
    r.username ||
    r.user_email ||
    (r.user_id != null ? `User #${r.user_id}` : 'Unknown user')
  );
};

const formatDateTime = (iso: string) => {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    time: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    millis: d.getTime(),
  };
};

// ---------- Trend chart ----------

type Granularity = 'day' | 'week' | 'month';

interface TrendPoint {
  label: string;
  fullLabel: string;
  count: number;
  bucketStart: Date;
}

const chooseGranularity = (rangeMs: number | null): Granularity => {
  if (rangeMs == null) return 'month';
  const days = rangeMs / (1000 * 60 * 60 * 24);
  if (days <= 45) return 'day';
  if (days <= 365) return 'week';
  return 'month';
};

const bucketKey = (date: Date, granularity: Granularity): string => {
  const d = new Date(date);
  if (granularity === 'day') {
    d.setHours(0, 0, 0, 0);
    return formatYmd(d);
  }
  if (granularity === 'week') {
    // ISO-ish week start (Sunday) bucket key
    const day = d.getDay();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - day);
    return formatYmd(d);
  }
  // month
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const advanceBucket = (date: Date, granularity: Granularity): Date => {
  const d = new Date(date);
  if (granularity === 'day') d.setDate(d.getDate() + 1);
  else if (granularity === 'week') d.setDate(d.getDate() + 7);
  else d.setMonth(d.getMonth() + 1);
  return d;
};

const bucketLabel = (date: Date, granularity: Granularity): string => {
  if (granularity === 'day') {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  if (granularity === 'week') {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

const bucketFullLabel = (date: Date, granularity: Granularity): string => {
  if (granularity === 'day') {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
  if (granularity === 'week') {
    const end = new Date(date);
    end.setDate(end.getDate() + 6);
    return `Week of ${date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    })} – ${end.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`;
  }
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const buildTrend = (
  items: SuperAdminRedemptionItem[],
  rangeStart: Date | null,
  rangeEnd: Date | null
): { points: TrendPoint[]; granularity: Granularity } => {
  if (items.length === 0 && !rangeStart && !rangeEnd) {
    return { points: [], granularity: 'day' };
  }

  // Determine effective range
  let start = rangeStart;
  let end = rangeEnd;
  if (!start || !end) {
    const sorted = [...items]
      .map(i => new Date(i.redeemed_at).getTime())
      .filter(t => Number.isFinite(t))
      .sort((a, b) => a - b);
    if (sorted.length > 0) {
      if (!start) start = new Date(sorted[0]);
      if (!end) end = new Date(sorted[sorted.length - 1]);
    }
  }
  if (!start || !end) {
    return { points: [], granularity: 'day' };
  }

  const granularity = chooseGranularity(end.getTime() - start.getTime());

  // Pre-aggregate counts per bucket
  const counts = new Map<string, number>();
  for (const item of items) {
    const d = new Date(item.redeemed_at);
    if (!Number.isFinite(d.getTime())) continue;
    if (d < start || d > end) continue;
    const key = bucketKey(d, granularity);
    counts.set(key, (counts.get(key) || 0) + 1);
  }

  // Walk buckets across the full range so the chart has consistent spacing,
  // including buckets with zero redemptions.
  const points: TrendPoint[] = [];
  let cursor = new Date(start);
  // Align cursor to bucket start
  const cursorKey = bucketKey(cursor, granularity);
  cursor = parseYmdToDate(cursorKey) || cursor;

  // Safety bound to avoid runaway loops on absurd ranges
  const MAX_POINTS = 400;
  while (cursor <= end && points.length < MAX_POINTS) {
    const key = bucketKey(cursor, granularity);
    points.push({
      label: bucketLabel(cursor, granularity),
      fullLabel: bucketFullLabel(cursor, granularity),
      count: counts.get(key) || 0,
      bucketStart: new Date(cursor),
    });
    cursor = advanceBucket(cursor, granularity);
  }

  return { points, granularity };
};

interface TrendChartProps {
  points: TrendPoint[];
  granularity: Granularity;
  loading: boolean;
}

const TrendChart: React.FC<TrendChartProps> = ({ points, granularity, loading }) => {
  const width = 960;
  const height = 260;
  const padding = { top: 24, right: 24, bottom: 36, left: 44 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const maxCount = useMemo(
    () => Math.max(1, ...points.map(p => p.count)),
    [points]
  );

  // Round max up to a nice number for y-axis
  const niceMax = useMemo(() => {
    if (maxCount <= 5) return Math.max(5, maxCount);
    const pow = Math.pow(10, Math.floor(Math.log10(maxCount)));
    const norm = maxCount / pow;
    let n;
    if (norm <= 1) n = 1;
    else if (norm <= 2) n = 2;
    else if (norm <= 5) n = 5;
    else n = 10;
    return n * pow;
  }, [maxCount]);

  const xFor = (i: number) => {
    if (points.length <= 1) return padding.left + innerW / 2;
    return padding.left + (i / (points.length - 1)) * innerW;
  };
  const yFor = (count: number) => {
    return padding.top + innerH - (count / niceMax) * innerH;
  };

  const linePath = useMemo(() => {
    if (points.length === 0) return '';
    return points
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i).toFixed(2)} ${yFor(p.count).toFixed(2)}`)
      .join(' ');
  }, [points, niceMax]); // eslint-disable-line react-hooks/exhaustive-deps

  const areaPath = useMemo(() => {
    if (points.length === 0) return '';
    const segs = points
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i).toFixed(2)} ${yFor(p.count).toFixed(2)}`)
      .join(' ');
    const baselineY = yFor(0);
    return `${segs} L ${xFor(points.length - 1).toFixed(2)} ${baselineY.toFixed(2)} L ${xFor(0).toFixed(2)} ${baselineY.toFixed(2)} Z`;
  }, [points, niceMax]); // eslint-disable-line react-hooks/exhaustive-deps

  const yTicks = useMemo(() => {
    const count = 4;
    return Array.from({ length: count + 1 }, (_, i) =>
      Math.round((niceMax / count) * i)
    );
  }, [niceMax]);

  // Down-sample x-axis labels so we never overlap
  const xLabelIndices = useMemo(() => {
    if (points.length === 0) return [] as number[];
    const target = 6;
    if (points.length <= target) {
      return points.map((_, i) => i);
    }
    const step = Math.ceil(points.length / target);
    const idx: number[] = [];
    for (let i = 0; i < points.length; i += step) idx.push(i);
    if (idx[idx.length - 1] !== points.length - 1) idx.push(points.length - 1);
    return idx;
  }, [points]);

  const totalInRange = useMemo(
    () => points.reduce((acc, p) => acc + p.count, 0),
    [points]
  );

  const handleMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (points.length === 0) return;
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    // Map mouse x (in CSS px) into the SVG's viewBox coordinate system
    const xInView = ((e.clientX - rect.left) / rect.width) * width;
    if (xInView < padding.left || xInView > padding.left + innerW) {
      setHoverIdx(null);
      return;
    }
    if (points.length === 1) {
      setHoverIdx(0);
      return;
    }
    const rel = (xInView - padding.left) / innerW;
    const idx = Math.round(rel * (points.length - 1));
    setHoverIdx(Math.max(0, Math.min(points.length - 1, idx)));
  };

  const hovered = hoverIdx != null ? points[hoverIdx] : null;

  return (
    <div className="trend-chart-card">
      <div className="trend-chart-header">
        <div>
          <h3>Redemptions Trend</h3>
          <p className="trend-chart-subtitle">
            {loading
              ? 'Loading trend…'
              : points.length === 0
              ? 'No data available for the selected filters.'
              : `${totalInRange.toLocaleString()} redemption${
                  totalInRange === 1 ? '' : 's'
                } · grouped by ${granularity}`}
          </p>
        </div>
        <div className="trend-chart-legend">
          <span className="legend-dot" />
          <span>Redemptions</span>
        </div>
      </div>

      <div className="trend-chart-svg-wrap">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          className="trend-chart-svg"
          onMouseMove={handleMove}
          onMouseLeave={() => setHoverIdx(null)}
        >
          <defs>
            <linearGradient id="trendArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#4ecdc4" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#4ecdc4" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Y gridlines + labels */}
          {yTicks.map((t, i) => {
            const y = yFor(t);
            return (
              <g key={`yt-${i}`}>
                <line
                  x1={padding.left}
                  x2={padding.left + innerW}
                  y1={y}
                  y2={y}
                  stroke="#e2e8f0"
                  strokeDasharray={i === 0 ? '0' : '3 3'}
                />
                <text
                  x={padding.left - 8}
                  y={y + 4}
                  textAnchor="end"
                  fontSize="11"
                  fill="#94a3b8"
                >
                  {t}
                </text>
              </g>
            );
          })}

          {/* X axis tick labels */}
          {xLabelIndices.map(i => (
            <text
              key={`xl-${i}`}
              x={xFor(i)}
              y={height - padding.bottom + 18}
              textAnchor="middle"
              fontSize="11"
              fill="#94a3b8"
            >
              {points[i].label}
            </text>
          ))}

          {/* Area + line */}
          {points.length > 1 && (
            <path d={areaPath} fill="url(#trendArea)" />
          )}
          {points.length > 0 && (
            <path
              d={linePath}
              fill="none"
              stroke="#0d9488"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}

          {/* Point markers */}
          {points.map((p, i) => (
            <circle
              key={`pt-${i}`}
              cx={xFor(i)}
              cy={yFor(p.count)}
              r={hoverIdx === i ? 5 : 3}
              fill="#0d9488"
              stroke="white"
              strokeWidth={hoverIdx === i ? 2 : 1}
            />
          ))}

          {/* Hover guide line */}
          {hovered && (
            <line
              x1={xFor(hoverIdx!)}
              x2={xFor(hoverIdx!)}
              y1={padding.top}
              y2={padding.top + innerH}
              stroke="#0d9488"
              strokeDasharray="4 4"
              strokeWidth={1}
            />
          )}

          {points.length === 0 && !loading && (
            <text
              x={width / 2}
              y={height / 2}
              textAnchor="middle"
              fill="#a0aec0"
              fontSize="13"
            >
              No redemptions in this range
            </text>
          )}
        </svg>

        {hovered && (
          <div
            className="trend-tooltip"
            style={{
              left: `${(xFor(hoverIdx!) / width) * 100}%`,
            }}
          >
            <div className="trend-tooltip-label">{hovered.fullLabel}</div>
            <div className="trend-tooltip-value">
              <span className="legend-dot small" />
              {hovered.count.toLocaleString()}{' '}
              redemption{hovered.count === 1 ? '' : 's'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Main view ----------

const AllRedemptionsView: React.FC = () => {
  const [businesses, setBusinesses] = useState<PublicBusiness[]>([]);
  const [businessesLoading, setBusinessesLoading] = useState(true);

  const [offerings, setOfferings] = useState<SuperAdminOffering[]>([]);
  const [offeringsLoading, setOfferingsLoading] = useState(true);

  // Data for the currently visible page
  const [items, setItems] = useState<SuperAdminRedemptionItem[]>([]);
  const [total, setTotal] = useState(0);

  // Separately fetched larger dataset used to draw the trend chart so that
  // the chart reflects every redemption matching the filters, not just the
  // current page.
  const [trendItems, setTrendItems] = useState<SuperAdminRedemptionItem[]>([]);
  const [trendTotal, setTrendTotal] = useState(0);

  const [loading, setLoading] = useState(true);
  const [trendLoading, setTrendLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [businessId, setBusinessId] = useState<number | ''>('');
  const [offeringId, setOfferingId] = useState<number | ''>('');
  const [userEmail, setUserEmail] = useState<string>('');
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('30d');
  const [customAfter, setCustomAfter] = useState<string>('');
  const [customBefore, setCustomBefore] = useState<string>('');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Resolve the active redeemed_after / redeemed_before window from preset or custom
  const dateRange = useMemo(() => {
    if (dateRangePreset === 'custom') {
      const after = customAfter ? toIsoStartOfDay(parseYmdToDate(customAfter)!) : undefined;
      const before = customBefore ? toIsoEndOfDay(parseYmdToDate(customBefore)!) : undefined;
      return { after, before };
    }
    return presetToDateRange(dateRangePreset);
  }, [dateRangePreset, customAfter, customBefore]);

  const rangeStartDate = useMemo(
    () => (dateRange.after ? new Date(dateRange.after) : null),
    [dateRange.after]
  );
  const rangeEndDate = useMemo(
    () => (dateRange.before ? new Date(dateRange.before) : null),
    [dateRange.before]
  );

  // The filters object sent to the API, excluding page/page_size which differ
  // for the table vs trend fetches.
  const sharedFilters: SuperAdminRedemptionFilters = useMemo(() => {
    return {
      business_id: typeof businessId === 'number' ? businessId : null,
      offering_id: typeof offeringId === 'number' ? offeringId : null,
      user_email: userEmail.trim() || null,
      redeemed_after: dateRange.after || null,
      redeemed_before: dateRange.before || null,
    };
  }, [businessId, offeringId, userEmail, dateRange]);

  // Stable JSON representation so memoization doesn't churn on every render
  const filterKey = useMemo(() => JSON.stringify(sharedFilters), [sharedFilters]);

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setPage(1);
  }, [filterKey]);

  // Load public businesses for the dropdown
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setBusinessesLoading(true);
        const data = await apiService.getPublicBusinesses();
        if (cancelled) return;
        const sorted = [...data].sort((a, b) => a.name.localeCompare(b.name));
        setBusinesses(sorted);
      } catch (err) {
        if (!cancelled) console.error('Failed to load businesses for filter:', err);
      } finally {
        if (!cancelled) setBusinessesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load all offerings (super admin scoped) for the dropdown
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setOfferingsLoading(true);
        const data = await apiService.getSuperAdminOfferings();
        if (cancelled) return;
        const sorted = [...data].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '')
        );
        setOfferings(sorted);
      } catch (err) {
        if (!cancelled) console.error('Failed to load offerings for filter:', err);
      } finally {
        if (!cancelled) setOfferingsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // If a business is selected, restrict the offering dropdown to that business.
  // Also drop the currently-selected offering if it no longer matches the
  // selected business so we don't ask the API for an offering that can't match.
  const visibleOfferings = useMemo(() => {
    if (typeof businessId !== 'number') return offerings;
    return offerings.filter(o => o.business_id === businessId);
  }, [offerings, businessId]);

  useEffect(() => {
    if (typeof offeringId !== 'number') return;
    if (!visibleOfferings.some(o => o.id === offeringId)) {
      setOfferingId('');
    }
  }, [visibleOfferings, offeringId]);

  // Fetch the paginated table data
  const fetchTablePage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getSuperAdminRedemptions({
        ...sharedFilters,
        page,
        page_size: pageSize,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (err) {
      console.error('Failed to fetch redemptions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load redemptions');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [sharedFilters, page, pageSize]);

  // Fetch the dataset used for the trend chart.
  //
  // The backend may silently ignore (or reject) an oversized `page_size`, so
  // we negotiate a working size by trying progressively smaller values until
  // one returns actual items. After settling on a size we fan out the
  // remaining pages in parallel.
  const fetchTrendData = useCallback(async () => {
    setTrendLoading(true);
    try {
      let chosenSize = 0;
      let first: SuperAdminRedemptionsResponse | null = null;

      for (const size of TREND_PAGE_SIZE_CANDIDATES) {
        try {
          const r = await apiService.getSuperAdminRedemptions({
            ...sharedFilters,
            page: 1,
            page_size: size,
          });

          // If the API rejected our size silently (no items even though more
          // exist), retry with a smaller size. Otherwise we found a winner.
          if (r.items.length === 0 && r.total > 0) {
            continue;
          }
          first = r;
          chosenSize = size;
          break;
        } catch (e) {
          // Try the next, smaller candidate
          console.warn(
            `Trend fetch with page_size=${size} failed, falling back:`,
            e
          );
        }
      }

      if (!first || chosenSize === 0) {
        setTrendItems([]);
        setTrendTotal(0);
        return;
      }

      const total = first.total;
      let allItems: SuperAdminRedemptionItem[] = first.items.slice();

      const neededRecords = Math.min(total, TREND_MAX_RECORDS);
      const totalPages = Math.max(1, Math.ceil(neededRecords / chosenSize));

      if (totalPages > 1) {
        const pageRequests: Promise<SuperAdminRedemptionsResponse>[] = [];
        for (let p = 2; p <= totalPages; p++) {
          pageRequests.push(
            apiService.getSuperAdminRedemptions({
              ...sharedFilters,
              page: p,
              page_size: chosenSize,
            })
          );
        }
        const results = await Promise.all(pageRequests);
        for (const r of results) {
          allItems = allItems.concat(r.items);
        }
      }

      setTrendItems(allItems);
      setTrendTotal(total);
    } catch (err) {
      console.error('Failed to fetch trend data:', err);
      setTrendItems([]);
      setTrendTotal(0);
    } finally {
      setTrendLoading(false);
    }
  }, [sharedFilters]);

  useEffect(() => {
    fetchTablePage();
  }, [fetchTablePage]);

  useEffect(() => {
    fetchTrendData();
  }, [fetchTrendData]);

  const { points: trendPoints, granularity } = useMemo(
    () => buildTrend(trendItems, rangeStartDate, rangeEndDate),
    [trendItems, rangeStartDate, rangeEndDate]
  );

  // Derived stats — based on the trend dataset for accuracy across pages
  const uniqueUsers = useMemo(
    () =>
      new Set(
        trendItems
          .map(r => (r.user_id != null ? `id:${r.user_id}` : r.user_email || ''))
          .filter(Boolean)
      ).size,
    [trendItems]
  );
  const uniqueBusinesses = useMemo(
    () => new Set(trendItems.map(r => r.business_id).filter(v => v != null)).size,
    [trendItems]
  );
  const uniqueOfferings = useMemo(
    () => new Set(trendItems.map(r => r.offering_id).filter(v => v != null)).size,
    [trendItems]
  );

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (sharedFilters.business_id != null) n++;
    if (sharedFilters.offering_id != null) n++;
    if (sharedFilters.user_email) n++;
    if (sharedFilters.redeemed_after || sharedFilters.redeemed_before) n++;
    return n;
  }, [sharedFilters]);

  const resetFilters = () => {
    setBusinessId('');
    setOfferingId('');
    setUserEmail('');
    setDateRangePreset('30d');
    setCustomAfter('');
    setCustomBefore('');
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (page <= 3) {
      pages.push(1, 2, 3, 4, '...', totalPages);
    } else if (page >= totalPages - 2) {
      pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
    }
    return pages;
  };

  const handleRefresh = () => {
    fetchTablePage();
    fetchTrendData();
  };

  // Helpful warnings about partial data in the chart
  const trendIsTruncated = trendTotal > trendItems.length;

  return (
    <div className="admin-view">
      <div className="view-header">
        <div>
          <h2>All Redemptions</h2>
          <p className="view-description">
            Inspect every redemption across the platform. Filters refine both
            the chart above and the table below.
          </p>
        </div>
        <button onClick={handleRefresh} className="add-button">
          🔄 Refresh
        </button>
      </div>

      <TrendChart
        points={trendPoints}
        granularity={granularity}
        loading={trendLoading}
      />

      {trendIsTruncated && (
        <div className="trend-warning">
          ⚠️ Showing the most recent {trendItems.length.toLocaleString()} of{' '}
          {trendTotal.toLocaleString()} redemptions in the chart. Narrow the
          date range or other filters for a complete trend.
        </div>
      )}

      {/* Filter panel */}
      <div className="filter-panel">
        <div className="filter-panel-header">
          <div>
            <strong>Filters</strong>
            {activeFilterCount > 0 && (
              <span className="active-filter-chip">
                {activeFilterCount} active
              </span>
            )}
          </div>
          {activeFilterCount > 0 && (
            <button className="link-button" onClick={resetFilters}>
              Clear all
            </button>
          )}
        </div>

        <div className="filter-grid">
          <div className="filter-field">
            <label htmlFor="filter-date-range">Date range</label>
            <select
              id="filter-date-range"
              value={dateRangePreset}
              onChange={e => setDateRangePreset(e.target.value as DateRangePreset)}
            >
              {DATE_RANGE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {dateRangePreset === 'custom' && (
            <>
              <div className="filter-field">
                <label htmlFor="filter-after">From</label>
                <input
                  id="filter-after"
                  type="date"
                  value={customAfter}
                  max={customBefore || undefined}
                  onChange={e => setCustomAfter(e.target.value)}
                />
              </div>
              <div className="filter-field">
                <label htmlFor="filter-before">To</label>
                <input
                  id="filter-before"
                  type="date"
                  value={customBefore}
                  min={customAfter || undefined}
                  onChange={e => setCustomBefore(e.target.value)}
                />
              </div>
            </>
          )}

          <div className="filter-field">
            <label htmlFor="filter-business">Business</label>
            <select
              id="filter-business"
              value={businessId === '' ? '' : String(businessId)}
              onChange={e =>
                setBusinessId(e.target.value === '' ? '' : Number(e.target.value))
              }
              disabled={businessesLoading}
            >
              <option value="">
                {businessesLoading ? 'Loading businesses…' : 'All businesses'}
              </option>
              {businesses.map(b => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-field">
            <label htmlFor="filter-offering">Offering</label>
            <select
              id="filter-offering"
              value={offeringId === '' ? '' : String(offeringId)}
              onChange={e =>
                setOfferingId(e.target.value === '' ? '' : Number(e.target.value))
              }
              disabled={offeringsLoading}
            >
              <option value="">
                {offeringsLoading
                  ? 'Loading offerings…'
                  : visibleOfferings.length === 0
                  ? typeof businessId === 'number'
                    ? 'No offerings for this business'
                    : 'No offerings available'
                  : 'All offerings'}
              </option>
              {visibleOfferings.map(o => {
                const businessSuffix =
                  typeof businessId === 'number'
                    ? ''
                    : o.business_name
                    ? ` — ${o.business_name}`
                    : '';
                return (
                  <option key={o.id} value={o.id}>
                    {o.name}{businessSuffix}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="filter-field">
            <label htmlFor="filter-user-email">User email</label>
            <input
              id="filter-user-email"
              type="email"
              placeholder="user@example.com"
              value={userEmail}
              onChange={e => setUserEmail(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-value">{trendTotal.toLocaleString()}</div>
          <div className="stat-label">Matching Redemptions</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{uniqueUsers.toLocaleString()}</div>
          <div className="stat-label">Unique Users</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{uniqueBusinesses.toLocaleString()}</div>
          <div className="stat-label">Businesses</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{uniqueOfferings.toLocaleString()}</div>
          <div className="stat-label">Offerings</div>
        </div>
      </div>

      {error && (
        <div className="error-message" style={{ marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div className="table-toolbar">
        <span className="table-toolbar-label">
          {loading
            ? 'Loading…'
            : total === 0
            ? 'No redemptions match the current filters'
            : `Showing ${(page - 1) * pageSize + 1}–${Math.min(
                page * pageSize,
                total
              )} of ${total.toLocaleString()}`}
        </span>
        <div className="table-toolbar-right">
          <label htmlFor="page-size-select" className="page-size-label">
            Rows per page
          </label>
          <select
            id="page-size-select"
            value={pageSize}
            onChange={e => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            {PAGE_SIZE_OPTIONS.map(size => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && items.length === 0 ? (
        <div className="loading-state">
          <p>Loading redemptions…</p>
        </div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          <p>No redemptions found.</p>
          {activeFilterCount > 0 && (
            <button className="add-button" onClick={resetFilters} style={{ marginTop: 16 }}>
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Business</th>
                  <th>Offering</th>
                  <th>Type</th>
                  <th>Redeemed At</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r, idx) => {
                  const name = displayName(r);
                  const { date, time, millis } = formatDateTime(r.redeemed_at);
                  const key =
                    r.id != null
                      ? `r-${r.id}`
                      : `r-${millis}-${r.user_id ?? r.user_email ?? ''}-${idx}`;
                  return (
                    <tr key={key}>
                      <td>
                        <div className="user-info-cell">
                          <div className="avatar">
                            {name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <strong>{name}</strong>
                            {r.user_email && r.user_email !== name && (
                              <div className="sub-text">{r.user_email}</div>
                            )}
                            {r.user_id != null && (
                              <div className="sub-text">ID: {r.user_id}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        {r.business_name ||
                          (r.business_id != null
                            ? `Business #${r.business_id}`
                            : '—')}
                      </td>
                      <td>
                        {r.offering_name ? (
                          <span className="type-badge">{r.offering_name}</span>
                        ) : r.offering_id != null ? (
                          <span className="type-badge">
                            Offering #{r.offering_id}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        {r.offering_type ? (
                          <span className="type-badge small">
                            {r.offering_type}
                          </span>
                        ) : (
                          <span style={{ color: '#a0aec0' }}>—</span>
                        )}
                      </td>
                      <td>
                        <div className="date-cell">
                          <div>{date}</div>
                          <div className="sub-text">{time}</div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-button"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1 || loading}
              >
                ← Previous
              </button>

              <div className="pagination-numbers">
                {getPageNumbers().map((p, i) =>
                  typeof p === 'number' ? (
                    <button
                      key={i}
                      className={`pagination-number ${page === p ? 'active' : ''}`}
                      onClick={() => handlePageChange(p)}
                      disabled={loading}
                    >
                      {p}
                    </button>
                  ) : (
                    <span key={i} className="pagination-ellipsis">
                      {p}
                    </span>
                  )
                )}
              </div>

              <button
                className="pagination-button"
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages || loading}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AllRedemptionsView;
